<script lang="ts" setup>
import { i18n } from '@/i18n';
import { CUSTOMER_TAB_ROUTER_INFO } from '../constants';
import ControlCustomerPageTab from '../components/ControlCustomerPageTab.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useCustomerForecastTimelineStore } from './useCustomerForecastTimelineStore';
import { storeToRefs } from 'pinia';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { computed, onMounted, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import ForecastTimelineTable from '@/corePagesModule/components/forecastTimelineTable/ForecastTimelineTable.vue';
import { CustomerForecastTimelineTableStateAdapter } from '../adapter/customerForecastTImelineTableStateAdapter';
import { CustomerForecastRepository } from '@/coreInfrastructureModule/Repositories/CustomerForecastRepository';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import { useRoute } from 'vue-router';
import DownloadTimelineModal from '../components/DownloadTimelineModal.vue';
import { CustomerForecastTimelineViewModel } from './customerForecastTimelineViewModel';

const t = i18n.t;
const locale = computed(() => i18n.locale);
const route = useRoute();
const store = useCustomerForecastTimelineStore();
const { state } = storeToRefs(store);
const repository = new CustomerForecastRepository();
const vm = new CustomerForecastTimelineViewModel();

onMounted(async () => {
  const businessId = typeof route.params.defaultBusinessId === 'string' ? route.params.defaultBusinessId : undefined;
  await init(businessId);
});

/**
 * 初期化処理
 * @param businessId - 取引ID
 */
const init = async (businessId?: string) => {
  await store.init(repository, businessId);
};

/**
 * 内示遍歴ページのまま取引IDが変更された際に再取得処理を行う
 * ex. 前月差異から遷移後、内示遍歴タブを押した場合、取引IDが選択されていない状態になるため、再度初期化
 */
watch(
  () => route.params.defaultBusinessId,
  async (newValue) => {
    const businessId = typeof newValue === 'string' ? newValue : undefined;
    await init(businessId);
  }
);

// フィルタ項目が変更された際に適用ボタンのdisabledを必要に応じて更新する
watch(
  () => state.value.filterContentsInfo.filterItems,
  (newValue) => {
    store.updateIsDisabledApplyFilterButton(newValue);
  },
  { deep: true }
);

/**
 * ダウンロードボタンのdisabled判定
 */
const isDisabledDownloadButton = computed(() => {
  return state.value.tableItems === undefined;
});
</script>
<template>
  <div class="customer-forecast-timeline">
    <div class="customer-forecast-timeline__top-container top-container">
      <ControlCustomerPageTab class="top-container__tab-bar" :tab-info-object="[...CUSTOMER_TAB_ROUTER_INFO]" />
    </div>
    <div class="customer-forecast-timeline__filter-container filter-container">
      <button class="filter-container__clear-button" @click="store.onClickClearFilterButton">
        {{ t('すべての選択状態をクリア') }}
      </button>
      <div class="filter-container__filter-contents">
        <template v-for="(item, index) in state.filterContentsInfo.filterItems" :key="item">
          <div class="filter-container__filter-item filter-item">
            <!-- <div class="filter-item__title">{{ item.label }}</div> -->
            <SingleSelectInput
              class="filter-item__select-input"
              :index="2 + index"
              :model-value="item.selectedValue"
              :placeholder="t('選択してください')"
              :label="item.i18nOptions ? t(item.label, item.i18nOptions) : t(item.label)"
              :filter-type="item.filterType"
              :is-open-prop="state.filterContentsInfo.currentFilterType === item.filterType"
              :prop-customer-forecast-filter-property="store.getFilterProperty()"
              use-teleport
              @click:select-input="() => (state.filterContentsInfo.currentFilterType = item.filterType)"
              @update:model-value="(e) => (state.filterContentsInfo.filterItems[index].selectedValue = e)"
            />
          </div>
        </template>
      </div>
      <div class="filter-container__footer">
        <TextButton
          class="filter-container__apply-button"
          :button-style="'btn-primary'"
          :size="'btn-md'"
          :on-click="() => store.onClickApplyFilterButton(repository)"
          :disabled="state.isDisabledApplyFilterButton"
        >
          {{ t('適用') }}
        </TextButton>
      </div>
    </div>
    <div class="customer-forecast-timeline__sub-button-container sub-button-container">
      <div class="sub-button-container__target-month-select">
        <MonthPicker
          class="sub-button-container__calendar customer-forecast-timeline-calendar"
          :model-value="state.targetStartYearMonth"
          :locale="locale"
          :prop-format="t('MonthPickerFormat')"
          @update:model-value="store.onSelectedStartYearMonth($event, repository)"
        />
        <!-- TODO: i18n適当なので直す -->
        {{ t('~') }}
        <SelectInput
          class="sub-button-container__select-input"
          :index="1"
          :model-value="t('displayMonthsOfData', { month: state.displayForecastsMonth })"
          :placeholder="''"
          :label="''"
          :filter-type="'DISPLAY_MONTH'"
          :prop-options="store.getMonthsDisplayForecast()"
          :is-open-prop="state.openedSelectInputType === 'DISPLAY_MONTH'"
          use-teleport
          @click:select-input="() => (state.openedSelectInputType = 'DISPLAY_MONTH')"
          @update:value-with-index="(_, index) => store.onSelectedDisplayForecastsMonth(index, repository)"
        />
      </div>
      <div class="sub-button-container__description-label-container description-label-container">
        <span class="description-label-container__label description-label-container__label--forecast">{{ t('黒文字：客先内示') }}</span>
        <div class="description-label-container__label-set-container">
          <span class="description-label-container__label description-label-container__label--sales-result">{{ t('青文字：') }}</span>
          <span
            class="description-label-container__label description-label-container__label--sales-result description-label-container__label--sales-result--suffix"
            >{{ t('販売実績') }}</span
          >
        </div>
      </div>
      <TextButton
        class="sub-button-container__download-button download-button"
        :disabled="true"
        @click="() => (state.downloadModalState.modalState = true)"
      >
        <div class="download-button__container">
          <p class="download-button__text">{{ t('ダウンロード') }}</p>
          <SvgLoader class="download-button__icon" name="fatArrowDown" />
        </div>
      </TextButton>
    </div>
    <ForecastTimelineTable
      class="customer-forecast-timeline__table-container"
      :header-month-list="state.targetYearMonthList"
      :row-month-list="state.receivedYearMonthList"
      :table-item-data="state.tableItems !== undefined ? CustomerForecastTimelineTableStateAdapter.adapt(state.tableItems) : undefined"
      :prop-loading-state="state.tableLoadingState"
      @reload="store.updateDisplayData(repository)"
    />
  </div>
  <!-- 関連取引ID取得のローディング -->
  <InfoModal :modal-state="isLoading(state.fetchBaseBusinessIdLoadingState.status)" icon="loading" :enable-outside-click="false">
    <template #title>{{ t('取引情報を取得中') }}</template>
  </InfoModal>
  <!-- ダウンロードモーダル -->
  <!-- TODO: 25-03-28 ダウンロード機能は未実装。一旦他タスクを優先し、後回しにするためコメントアウト。 -->
  <!-- <DownloadTimelineModal
    v-if="state.downloadModalState.modalState"
    :store="store"
    :default-start-year-month="state.targetStartYearMonth"
    :default-end-year-month="vm.calculateEndYearMonth(state.targetStartYearMonth, state.displayForecastsMonth)"
  /> -->
</template>

<style scoped lang="scss">
.customer-forecast-timeline {
  width: 100%;
  min-height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: repeat(3, auto) minmax(0, 1fr);
  gap: 16px;
  font: Noto Sans JP;
  letter-spacing: 0;
  white-space: nowrap;
}

.top-container {
  display: grid;
  grid-auto-flow: column;
  align-items: start;
  justify-content: start;
  gap: 16px;
}

.filter-container {
  display: grid;
  justify-items: start;
  gap: 16px;
  padding: 16px;
  background-color: $surfaceDefault;
  border-radius: 4px;

  &__clear-button {
    height: max-content;
    font-size: 16px;
    line-height: 16px;
    font-weight: 400;
    color: $semanticPrimary;
    border-bottom: 1px solid $semanticPrimary;

    &:hover {
      opacity: 0.8;
    }
  }

  &__filter-contents {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__filter-item {
    flex-basis: 160px;
  }

  &__footer {
    width: 100%;
    display: grid;
    place-items: center;
  }

  &__apply-button {
    width: 160px;
    height: 40px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    padding: 6px 56px 6px 56px;
  }
}

.sub-button-container {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 16px;
  justify-content: start;
  align-items: center;

  &__target-month-select {
    display: grid;
    grid-template-columns: repeat(3, auto);
    gap: 8px;
    align-items: center;
    font-size: $fontSizeS;
    font-weight: 400;
  }

  &__calendar {
    min-width: 141px;
    max-width: 141px;
    height: 100%;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  &__select-input {
    min-width: 162px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}

.description-label-container {
  display: grid;
  grid-auto-flow: column;
  gap: 24px;
  font-size: $fontSizeS;
  font-weight: 400;
  background-color: $surfaceDefault;
  border-radius: 4px;
  padding: 8px;

  &__label-set-container {
    display: grid;
    grid-auto-flow: column;
  }

  &__label {
    font-size: $fontSizeS;
    font-weight: 400;

    &--forecast {
      color: $textSecondary;
    }

    &--sales-result {
      color: $semanticPrimary;

      &--suffix {
        font-weight: 700;
      }
    }
  }
}

.download-button {
  width: 156px;
  height: 34px;
  padding: 0 17px;
  display: grid;
  place-items: center;
  font-size: $fontSizeM;
  line-height: 22px;
  background-color: $semanticWhite;
  border: 1px solid $semanticPrimary;
  color: $semanticPrimary;

  &__container {
    display: grid;
    grid-template-columns: auto 22px;
    place-items: center;
    gap: 4px;
  }
}
</style>

<style lang="scss">
.customer-forecast-timeline-calendar input {
  font-family: Noto Sans JP;
  font-size: $fontSizeM;
  font-weight: 400;
  color: $textDefault;
}
</style>
